import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import { getNotificationsRequest, NotificationsLoadingKeys, markAsReadUnreadRequest, cleanUpNotificationsData, markAllAsReadRequest, markBulkAsReadUnreadRequest } from "../../slices/managementSlices/notificationsSlice";
import { Pagination } from "../../components/Pagination";
import { arrayHasItems } from "../../helpers/common";
import { SelectDropdown } from "../../components/SelectDropdown";
import { OverlayGrayBackground } from "../../components/OverlayGrayBackground";
import { LoaderSpinner } from "../../components/LoaderSpinner";
import { enumInventoryStatuses } from "../../helpers/notifications/consts";
import { ConfrimModal } from "../../components/ConfrimModal";
import { BulkEditModal } from "../../components/notifications/BulkEditModal";

const Notifications = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.auth);
    const currentUserId = currentUser?.id;
    const {
        pageNotifications,
        loadingKeys: notificationsLoadingKeys,
        loading: notificationsLoading
    } = useSelector((state) => state.notifications);
    const notifications = pageNotifications?.list || [];
    const totalCount = pageNotifications?.totalCount || 0;
    const unreadCount = pageNotifications?.unreadCount || 0;

    const [currentPage, setCurrentPage] = useState(1);
    const [currentItemsPerPage, setCurrentItemsPerPage] = useState(25);
    const [filter, setFilter] = useState('ALL');
    const [categoryFilter, setCategoryFilter] = useState('ALL');
    const [openMarkAllAsReadModal, setOpenMarkAllAsReadModal] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [openBulkModal, setOpenBulkModal] = useState(false);
    const [bulkAction, setBulkAction] = useState('READ');

    // useEffect(() => {
    //     return () => {
    //         dispatch(cleanUpNotificationsData({}));
    //     }
    // }, [currentUserId]);

    useEffect(() => {
        if (currentUserId) {
            dispatch(getNotificationsRequest({ userId: currentUserId, all: true }));
        }
    }, [currentUserId]);

    const isLoading = notificationsLoading && notificationsLoadingKeys.includes(NotificationsLoadingKeys.getNotifications);
    const isMarkAllAsReadLoading = notificationsLoading && notificationsLoadingKeys.includes(NotificationsLoadingKeys.markAllAsRead);
    const isBulkLoading = notificationsLoading && notificationsLoadingKeys.includes(NotificationsLoadingKeys.markAsRead);

    const filtered = useMemo(() => {
        if (!arrayHasItems(notifications)) return [];
        let out = notifications;
        if (filter === 'UNREAD') out = out.filter(n => !n.readed);
        if (filter === 'READ') out = out.filter(n => !!n.readed);
        if (categoryFilter !== 'ALL') out = out.filter(n => n.category === categoryFilter);
        return out;
    }, [notifications, filter, categoryFilter]);

    const totalPages = useMemo(() => {
        if (!arrayHasItems(filtered)) return 1;
        return Math.max(1, Math.ceil(filtered.length / Number(currentItemsPerPage)));
    }, [filtered, currentItemsPerPage]);

    const tableWrapperRef = useRef(null);
    const tableRef = useRef(null);
    const tableHeaderRef = useRef(null);

    useEffect(() => {
        const wrapper = tableWrapperRef.current;
        const head = tableHeaderRef.current;
        if (!wrapper || !head) return;

        const handleTableScroll = () => {
            const scrollTop = wrapper.scrollTop;
            const headerRows = head.children;
            for (let i = 0; i < headerRows.length; i++) {
                headerRows[i].style.transform = `translateY(${scrollTop}px)`;
            }
        };

        handleTableScroll();
        wrapper.addEventListener('scroll', handleTableScroll, { passive: true });
        return () => {
            wrapper.removeEventListener('scroll', handleTableScroll);
        };
    }, [filtered, currentItemsPerPage]);

    const pageItems = useMemo(() => {
        const start = (currentPage - 1) * Number(currentItemsPerPage);
        const end = start + Number(currentItemsPerPage);
        return filtered.slice(start, end);
    }, [filtered, currentPage, currentItemsPerPage]);

    const rangeRows = useMemo(() => {
        if (!arrayHasItems(filtered)) return '0-0 of 0';
        const start = (currentPage - 1) * Number(currentItemsPerPage) + 1;
        const end = Math.min(filtered.length, currentPage * Number(currentItemsPerPage));
        return `${start}-${end} of ${filtered.length}`;
    }, [filtered, currentPage, currentItemsPerPage]);

    const isTheLastPage = currentPage >= totalPages;

    const toggleRead = async (notification) => {
        await dispatch(
            markAsReadUnreadRequest({
                userId: currentUserId,
                notificationId: notification.notification_id,
                readed: String(!notification.readed)
            })
        ).unwrap();
        await dispatch(getNotificationsRequest({ userId: currentUserId, all: true })).unwrap();
        await dispatch(getNotificationsRequest({ userId: currentUserId, all: false })).unwrap();
    };

    const getCategoryPill = (category) => {
        if (category === 'INVENTORY') return { label: 'Inventory', cls: 'bg-indigo-50 text-indigo-700' };
        if (category === 'UTILITIES') return { label: 'Utilities', cls: 'bg-purple-50 text-purple-700' };
        if (category === 'EMAILS') return { label: 'Emails', cls: 'bg-green-50 text-green-700' };
        if (category === 'RENEWALS') return { label: 'Renewals', cls: 'bg-amber-50 text-amber-700' };
        return { label: category, cls: 'bg-gray-100 text-gray-700' };
    };

    return (
        <main className="w-full px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            {isLoading && <OverlayGrayBackground />}
            <section className="bg-white overflow-hidden relative mt-8 h-full mb-8">
                <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-semibold text-gray-900">Notifications</h3>
                </div>
                <div className="mt-6">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <div className="text-sm text-gray-700 flex items-center gap-2">
                            <span className="font-medium">Total:</span> {isLoading ? <LoaderSpinner width="15" height="15" border="2" colorSpinner="white" /> : totalCount}
                            <span className="mx-2">•</span>
                            <span className="font-medium">Unread:</span> {isLoading ? <LoaderSpinner width="15" height="15" border="2" colorSpinner="white" /> : unreadCount}
                        </div>
                        <div className="ml-auto w-48">
                            <SelectDropdown
                                options={[
                                    { value: 'ALL', label: 'All' },
                                    { value: 'UNREAD', label: 'Unread' },
                                    { value: 'READ', label: 'Read' },
                                ]}
                                value={filter}
                                onChange={(val) => {
                                    setCurrentPage(1);
                                    setFilter(val);
                                }}
                                selectClassname="!p-1.5"
                            />
                        </div>
                        <div className="w-48">
                            <SelectDropdown
                                options={[
                                    { value: 'ALL', label: 'All categories' },
                                    ...Object.entries(enumInventoryStatuses)
                                        .map(([value, label]) => ({ value, label })),
                                ]}
                                value={categoryFilter}
                                onChange={(val) => {
                                    setCurrentPage(1);
                                    setCategoryFilter(val);
                                }}
                                selectClassname="!p-1.5"
                            />
                        </div>
                        <div className="w-32 mr-1">
                            <button
                                type="button"
                                disabled={isMarkAllAsReadLoading || unreadCount === 0}
                                onClick={() => setOpenMarkAllAsReadModal(true)}
                                className="text-sm text-white px-3 py-1.5 rounded-md bg-blue-700 hover:bg-blue-500 transition-colors duration-200 min-w-[130px] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isMarkAllAsReadLoading ? <LoaderSpinner width="15" height="15" border="2" colorSpinner="white" /> : 'Mark all as Read'}
                            </button>
                        </div>
                        <div>
                            <button
                                type="button"
                                disabled={selectedIds.size === 0 || isBulkLoading}
                                onClick={() => setOpenBulkModal(true)}
                                className="text-sm text-white px-3 py-1.5 rounded-md bg-blue-700 hover:bg-blue-500 transition-colors duration-200 min-w-[110px] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isBulkLoading ? <LoaderSpinner width="15" height="15" border="2" colorSpinner="white" /> : 'Bulk Edit'}
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto w-full">
                        <div className="overflow-y-auto max-h-[calc(100vh-320px)] relative" ref={tableWrapperRef}>
                            <table ref={tableRef} className="min-w-full divide-y divide-gray-200 relative">
                            <thead ref={tableHeaderRef} className="bg-gray-50 z-10 relative">
                                <tr className="bg-gray-50">
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[60px] w-[60px]">
                                        <input
                                            type="checkbox"
                                            checked={pageItems.length > 0 && pageItems.every(item => selectedIds.has(item.notification_id || item.id))}
                                            onChange={(e) => {
                                                const next = new Set(selectedIds);
                                                if (e.target.checked) {
                                                    pageItems.forEach((n) => next.add(n.notification_id || n.id));
                                                } else {
                                                    pageItems.forEach((n) => next.delete(n.notification_id || n.id));
                                                }
                                                setSelectedIds(next);
                                            }}
                                        />
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">Category</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">Title</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[160px]">Created</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">Status</th>
                                    <th className="px-4 py-2 min-w-[180px]" />
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {!arrayHasItems(pageItems) ? (
                                    <tr>
                                        <td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={6}>
                                            No notifications found.
                                        </td>
                                    </tr>
                                ) : (
                                    pageItems.map((n) => {
                                        const pill = getCategoryPill(n.category);
                                        return (
                                            <tr key={n.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-2 whitespace-nowrap w-[60px] min-w-[60px] max-w-[60px]">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.has(n.notification_id || n.id)}
                                                        onChange={(e) => {
                                                            const key = n.notification_id || n.id;
                                                            setSelectedIds((prev) => {
                                                                const copy = new Set(prev);
                                                                if (e.target.checked) copy.add(key);
                                                                else copy.delete(key);
                                                                return copy;
                                                            });
                                                        }}
                                                    />
                                                </td>
                                                <td className="px-4 py-2 whitespace-nowrap w-[140px] min-w-[140px] max-w-[140px]">
                                                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${pill.cls}`}>
                                                        {pill.label}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2 w-[420px] min-w-[420px]">
                                                    <div className="text-sm font-medium text-gray-900 truncate" title={n.title}>{n.title}</div>
                                                    {n.details && (
                                                        <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                                                            <div>{n.details}</div>
                                                            {typeof n.url === 'string' && n.url.startsWith('/') && (
                                                                <a href={n.url} className="text-xs text-primary underline">
                                                                    Open in Eadin
                                                                </a>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap min-w-[160px]">{n.created_at}</td>
                                                <td className="px-4 py-2 whitespace-nowrap min-w-[140px]">
                                                    {n.readed ? (
                                                        <span className="inline-flex items-center rounded-full bg-green-200 text-green-700 px-2 py-0.5 text-xs">Read</span>
                                                    ) : (
                                                        <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 px-2 py-0.5 text-xs">Unread</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 text-right whitespace-nowrap min-w-[180px]">
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleRead(n)}
                                                        className="text-sm text-white px-3 py-1 rounded-md bg-blue-700 hover:bg-blue-500 transition-colors duration-200 min-w-[130px]"
                                                    >
                                                        Mark as {n.readed ? 'Unread' : 'Read'}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                            </table>
                        </div>
                    </div>

                    <Pagination
                        currentItemsPerPage={currentItemsPerPage}
                        rangeRows={rangeRows}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        isTheLastPage={isTheLastPage}
                        totalPages={totalPages}
                        setCurrentItemsPerPage={setCurrentItemsPerPage}
                        testId="notifications_table"
                    />
                </div>
            </section>
            <BulkEditModal
                isOpen={openBulkModal}
                isLoading={isBulkLoading}
                selectedCount={selectedIds.size}
                actionValue={bulkAction}
                onActionChange={(val) => setBulkAction(val)}
                onSubmit={async () => {
                    const ids = Array.from(selectedIds);
                    if (ids.length === 0) return;
                    setOpenBulkModal(false);
                    await dispatch(
                        markBulkAsReadUnreadRequest({
                            userId: currentUserId,
                            notificationIds: ids,
                            readed: bulkAction === 'READ'
                        })
                    ).unwrap();
                    await dispatch(getNotificationsRequest({ userId: currentUserId, all: true })).unwrap();
                    await dispatch(getNotificationsRequest({ userId: currentUserId, all: false })).unwrap();
                    setSelectedIds(new Set());
                }}
                onCancel={() => setOpenBulkModal(false)}
            />
            <ConfrimModal
                isOpen={openMarkAllAsReadModal}
                title={"Are you sure you want to mark all notifications as read?"}
                confirmButtonTitle={"Yes"}
                isLoading={isMarkAllAsReadLoading}
                submitClick={async () => {
                    setOpenMarkAllAsReadModal(false);
                    await dispatch(
                        markAllAsReadRequest({
                            userId: currentUserId,
                        })
                    ).unwrap();
                }}
                cancelClick={() => {
                    setOpenMarkAllAsReadModal(false);
                }}
            />
        </main>
    );
};

export default Notifications;